<script setup>
import ConnectorIcon from 'dashboard/components-next/agents/ConnectorIcon.vue';
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConnectorForm from './ConnectorForm.vue';
import ConnectorsAPI from 'dashboard/api/agents/connectors';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();
// Confirmação do sistema (no lugar do confirm() do navegador).
const confirmRef = ref(null);

const editingConnector = ref(null);
const selectedType = ref(null);
const formDialogRef = ref(null);
const typeSelectorRef = ref(null);

const connectors = computed(() => store.getters['aiConnectors/getConnectors']);
const connectorTypes = computed(
  () => store.getters['aiConnectors/getConnectorTypes']
);
const uiFlags = computed(() => store.getters['aiConnectors/getUIFlags']);

const getCategoryName = category => {
  const names = {
    erp: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.ERP'),
    calendar: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.CALENDAR'),
    credit: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.CREDIT'),
    automation: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.AUTOMATION'),
    crm: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.CRM'),
    messaging: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.MESSAGING'),
    chatbot: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.CHATBOT'),
    billing: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.BILLING'),
    other: t('AI_AGENTS.AGENT_CONNECTORS.CATEGORIES.OTHER'),
  };
  return names[category] || category;
};

const getCategoryIcon = category => {
  const icons = {
    erp: 'i-lucide-database',
    calendar: 'i-lucide-calendar',
    credit: 'i-lucide-credit-card',
    automation: 'i-lucide-zap',
    crm: 'i-lucide-users',
    messaging: 'i-lucide-message-circle',
    chatbot: 'i-lucide-bot',
    other: 'i-lucide-puzzle',
  };
  return icons[category] || 'i-lucide-plug';
};

// Tipos disponíveis (não configurados ainda)
const availableTypes = computed(() => {
  const types = connectorTypes.value || {};
  const configuredTypes = connectors.value.map(c => c.connector_type);

  return Object.entries(types)
    .filter(([key]) => !configuredTypes.includes(key))
    .map(([key, info]) => ({
      key,
      ...info,
    }));
});

// Tipos organizados por categoria (para o seletor)
const typesByCategory = computed(() => {
  const categories = {};

  availableTypes.value.forEach(item => {
    const category = item.category || 'other';
    if (!categories[category]) {
      categories[category] = {
        name: getCategoryName(category),
        icon: getCategoryIcon(category),
        items: [],
      };
    }
    categories[category].items.push(item);
  });

  return categories;
});

const getTypeIcon = type => {
  const icons = {
    ixc: 'i-lucide-server',
    mk_auth: 'i-lucide-shield',
    radius_net: 'i-lucide-wifi',
    sgp: 'i-lucide-building',
    credito_na_medida: 'i-lucide-credit-card',
    google_calendar: 'i-lucide-calendar',
    calendly: 'i-lucide-calendar-clock',
    n8n: 'i-lucide-workflow',
    custom_webhook: 'i-lucide-webhook',
    hubspot: 'i-lucide-target',
    pipedrive: 'i-lucide-bar-chart',
    evolution_api: 'i-lucide-message-circle',
    typebot: 'i-lucide-bot',
  };
  return icons[type] || 'i-lucide-plug';
};

const fetchConnectors = async () => {
  try {
    await store.dispatch('aiConnectors/fetchConnectors');
  } catch (error) {
    useAlert(t('AI_AGENTS.CONNECTORS.ERRORS.FETCH'));
  }
};

const openTypeSelector = () => {
  typeSelectorRef.value?.open();
};

const selectType = type => {
  selectedType.value = type.key;
  editingConnector.value = null;
  typeSelectorRef.value?.close();
  formDialogRef.value?.open();
};

const openEditForm = connector => {
  selectedType.value = connector.connector_type;
  editingConnector.value = connector;
  formDialogRef.value?.open();
};

const closeForm = () => {
  formDialogRef.value?.close();
  editingConnector.value = null;
  selectedType.value = null;
};

const handleSave = async connector => {
  try {
    if (editingConnector.value) {
      await store.dispatch('aiConnectors/updateConnector', {
        id: editingConnector.value.id,
        ...connector,
      });
      useAlert(t('AI_AGENTS.CONNECTORS.UPDATED'));
    } else {
      await store.dispatch('aiConnectors/createConnector', connector);
      useAlert(t('AI_AGENTS.CONNECTORS.CREATED'));
    }
    closeForm();
  } catch (error) {
    useAlert(t('AI_AGENTS.CONNECTORS.ERRORS.SAVE'));
  }
};

const handleDelete = async connector => {
  const ok = await confirmRef.value?.ask({
    title: t('AI_AGENTS.CONFIRM_DIALOG.DELETE_CONNECTOR_TITLE'),
    description: t('AI_AGENTS.CONNECTORS.DELETE_CONFIRM'),
    confirmLabel: t('AI_AGENTS.CONFIRM_DIALOG.DELETE_CONFIRM'),
  });
  if (!ok) return;
  try {
    await store.dispatch('aiConnectors/deleteConnector', connector.id);
    useAlert(t('AI_AGENTS.CONNECTORS.DELETED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.CONNECTORS.ERRORS.DELETE'));
  }
};

const registerSiengeWebhooks = async connector => {
  const confirmed = await confirmRef.value?.ask({
    title: t('SIENGE.WEBHOOKS'),
    description: t('SIENGE.WEBHOOK_HELP'),
  });
  if (!confirmed) return;
  try {
    const { data } = await ConnectorsAPI.registerSiengeWebhooks(connector.id);
    useAlert(data.message || data.error);
    await fetchConnectors();
  } catch (e) {
    useAlert(e.response?.data?.error || t('SIENGE.ERROR'));
  }
};

const handleTestConnection = async connector => {
  try {
    const result = await store.dispatch(
      'aiConnectors/testConnection',
      connector.id
    );
    if (result.success) {
      useAlert(t('AI_AGENTS.CONNECTORS.CONNECTION_SUCCESS'));
    } else {
      useAlert(result.error || t('AI_AGENTS.CONNECTORS.CONNECTION_FAILED'));
    }
  } catch (error) {
    useAlert(t('AI_AGENTS.CONNECTORS.CONNECTION_FAILED'));
  }
};

const getStatusClass = connector => {
  if (!connector.is_active) return 'bg-n-alpha-2 text-n-slate-11';
  if (connector.last_error_at) return 'bg-n-ruby-3 text-n-ruby-11';
  if (connector.last_sync_at) return 'bg-n-teal-3 text-n-teal-11';
  return 'bg-n-amber-3 text-n-amber-11';
};

const getStatusText = connector => {
  if (!connector.is_active) return t('AI_AGENTS.CONNECTORS.STATUS.INACTIVE');
  if (connector.last_error_at) return t('AI_AGENTS.CONNECTORS.STATUS.ERROR');
  if (connector.last_sync_at) return t('AI_AGENTS.CONNECTORS.STATUS.ACTIVE');
  return t('AI_AGENTS.CONNECTORS.STATUS.PENDING');
};

onMounted(() => {
  fetchConnectors();
});
</script>

<template>
  <div
    class="flex flex-col gap-6 p-4 sm:p-6 h-full overflow-y-auto bg-n-background"
  >
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.CONNECTORS.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.CONNECTORS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="availableTypes.length > 0"
        :label="t('AI_AGENTS.CONNECTORS.ADD')"
        icon="i-lucide-plus"
        @click="openTypeSelector"
      />
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="flex justify-center py-12">
      <Spinner :size="32" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="connectors.length === 0"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 rounded-2xl border border-n-weak"
    >
      <div
        class="w-20 h-20 rounded-2xl bg-gradient-to-br from-n-brand/20 to-n-brand/5 flex items-center justify-center mb-6"
      >
        <span class="i-lucide-plug text-4xl text-n-brand" />
      </div>
      <p class="text-lg font-semibold text-n-slate-12">
        {{ t('AI_AGENTS.CONNECTORS.EMPTY.TITLE') }}
      </p>
      <p class="mt-2 text-sm text-n-slate-11 max-w-md text-center">
        {{ t('AI_AGENTS.CONNECTORS.EMPTY.DESCRIPTION') }}
      </p>
      <Button
        :label="t('AI_AGENTS.CONNECTORS.ADD_FIRST')"
        icon="i-lucide-plus"
        class="mt-6"
        @click="openTypeSelector"
      />
    </div>

    <!-- Configured Connectors List -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
      <div
        v-for="connector in connectors"
        :key="connector.id"
        class="bg-n-solid-1 rounded-2xl border border-n-weak p-5 hover:shadow-md transition-shadow"
      >
        <div class="flex items-start gap-4">
          <!-- Icon -->
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            :class="
              connector.is_active && !connector.last_error_at
                ? 'bg-n-teal-3'
                : connector.last_error_at
                  ? 'bg-n-ruby-3'
                  : 'bg-n-alpha-2'
            "
          >
            <ConnectorIcon
              :type="connector.connector_type"
              :icon="getTypeIcon(connector.connector_type)"
              class="w-6 h-6"
              :class="[
                connector.is_active && !connector.last_error_at
                  ? 'text-n-teal-11'
                  : connector.last_error_at
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-11',
              ]"
            />
          </div>

          <!-- Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-base font-medium text-n-slate-12 truncate">
                {{ connector.name }}
              </h3>
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="getStatusClass(connector)"
              >
                {{ getStatusText(connector) }}
              </span>
            </div>
            <p class="text-sm text-n-slate-11 mt-0.5">
              {{ connector.type_name }}
            </p>
            <p
              class="text-xs text-n-slate-10 mt-1 truncate"
              :title="connector.api_url"
            >
              {{ connector.api_url }}
            </p>

            <!-- Tools count -->
            <div
              v-if="connector.available_tools?.length"
              class="flex items-center gap-1 mt-2 text-xs text-n-slate-10"
            >
              <span class="i-lucide-wrench w-3 h-3" />
              {{ connector.available_tools.length }}
              {{ t('AI_AGENTS.CONNECTORS.TOOLS') }}
            </div>

            <!-- Error message -->
            <p
              v-if="connector.last_error_message"
              class="mt-2 text-xs text-n-ruby-11 truncate"
              :title="connector.last_error_message"
            >
              {{ connector.last_error_message }}
            </p>
          </div>
        </div>

        <!-- Actions -->
        <Button
          v-if="
            connector.connector_type === 'sienge' &&
            connector.is_active &&
            !connector.config?.webhook_status
          "
          :label="t('SIENGE.WEBHOOKS')"
          variant="ghost"
          @click="registerSiengeWebhooks(connector)"
        />
        <div
          class="flex items-center justify-end gap-1 mt-4 pt-4 border-t border-n-weak"
        >
          <Button
            icon="i-lucide-plug"
            size="sm"
            variant="ghost"
            color="slate"
            :is-loading="uiFlags.isTesting"
            :title="t('AI_AGENTS.CONNECTORS.TEST')"
            @click="handleTestConnection(connector)"
          />
          <Button
            icon="i-lucide-pencil"
            size="sm"
            variant="ghost"
            color="slate"
            :title="t('AI_AGENTS.CONNECTORS.EDIT')"
            @click="openEditForm(connector)"
          />
          <Button
            icon="i-lucide-trash-2"
            size="sm"
            variant="ghost"
            color="ruby"
            :title="t('AI_AGENTS.CONNECTORS.DELETE')"
            @click="handleDelete(connector)"
          />
        </div>
      </div>
    </div>

    <!-- Type Selector Dialog -->
    <Dialog
      ref="typeSelectorRef"
      :title="t('AI_AGENTS.CONNECTORS.SELECT_TYPE')"
      width="2xl"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="space-y-6 max-h-[60vh] overflow-y-auto">
        <div
          v-for="(category, categoryKey) in typesByCategory"
          :key="categoryKey"
        >
          <div class="flex items-center gap-2 mb-3">
            <span class="w-5 h-5 text-n-slate-11" :class="[category.icon]" />
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ category.name }}
            </h4>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <button
              v-for="item in category.items"
              :key="item.key"
              type="button"
              class="flex items-center gap-3 p-3 bg-n-solid-2 rounded-xl border border-n-weak hover:border-n-brand hover:bg-n-alpha-1 transition-all text-left"
              @click="selectType(item)"
            >
              <div class="p-2 rounded-lg bg-n-solid-1 border border-n-weak">
                <ConnectorIcon
                  :type="item.key"
                  :icon="getTypeIcon(item.key)"
                  class="w-5 h-5 text-n-slate-11"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ item.name }}
                </p>
                <p class="text-xs text-n-slate-11 truncate">
                  {{ item.description }}
                </p>
                <p
                  v-if="item.tools?.length"
                  class="text-xs text-n-brand mt-0.5"
                >
                  {{ item.tools.length }}
                  {{ t('AI_AGENTS.CONNECTORS.TOOLS') }}
                </p>
              </div>
              <span class="i-lucide-chevron-right w-4 h-4 text-n-slate-10" />
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="Object.keys(typesByCategory).length === 0"
          class="text-center py-8"
        >
          <span class="i-lucide-check-circle text-4xl text-n-teal-11" />
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('AI_AGENTS.CONNECTORS.ALL_CONFIGURED') }}
          </p>
        </div>
      </div>
    </Dialog>

    <!-- Form Dialog -->
    <Dialog
      ref="formDialogRef"
      :title="
        editingConnector
          ? t('AI_AGENTS.CONNECTORS.EDIT')
          : t('AI_AGENTS.CONNECTORS.NEW')
      "
      width="2xl"
      :show-confirm-button="false"
      :show-cancel-button="false"
      @close="closeForm"
    >
      <ConnectorForm
        :connector="editingConnector"
        :connector-types="connectorTypes"
        :selected-type="selectedType"
        :is-saving="uiFlags.isCreating || uiFlags.isUpdating"
        @save="handleSave"
        @cancel="closeForm"
      />
    </Dialog>
  </div>
  <ConfirmDialog ref="confirmRef" />
</template>
